<template>
  <div
    class="flex flex-col md:rounded-2xl shadow-2xl lg:w-2xl md:w-3xl w-full bg-white min-h-0 max-h-svh md:max-h-[95vh] hover-scroll"
  >
    <div
      class="shrink-0 flex items-center justify-between md:px-6 h-16 px-5 border-b border-b-gray-100"
    >
      <button
        type="button"
        class="transition-all duration-300 cursor-pointer md:hidden flex items-center justify-center"
        @click="handleClose"
      >
        <Icon name="tabler:arrow-narrow-left" size="24" />
      </button>
      <div class="md:block hidden"></div>
      <p class="font-semibold sm:text-lg">
        {{ $t("new.new_artwork") }}
      </p>
      <button
        type="button"
        class="transition-all duration-300 cursor-pointer size-10 min-w-10 rounded-full md:flex hidden items-center justify-center bg-gray-100 hover:bg-gray-300"
        @click="handleClose"
      >
        <Icon name="ph:x" size="16" />
      </button>
      <div class="md:hidden block w-10"></div>
    </div>

    <div
      class="md:px-6 px-2 flex-1 min-h-0 overflow-y-auto pb-4 overflow-x-hidden"
    >
      <div class="flex items-center md:py-6 py-5">
        <div class="flex items-center space-x-2">
          <div
            class="size-7 min-w-7 flex items-center justify-center text-xs bg-gray-100 rounded-full"
          >
            {{ store.currentStep + 1 }}
          </div>
          <span class="font-normal sm:text-sm text-xs text-gray-600">{{
            $t(`${stepTitle}`)
          }}</span>
        </div>
      </div>
      <div class="sm:p-0 p-2">
        <slot></slot>
      </div>
    </div>

    <div
      class="w-full flex items-center justify-between shrink-0 px-5 md:px-6 py-4 bg-transparent border-t border-t-gray-200"
    >
      <button
        v-if="store.currentStep > 0"
        class="flex items-center justify-center cursor-pointer"
        @click="store.prevStep"
      >
        <Icon
          name="tabler:arrow-narrow-left"
          size="24"
          class="scale-100 hover:scale-105"
        />
      </button>

      <button
        class="flex items-center justify-center space-x-1"
        :class="[
          store.currentStep > 0 ? 'w-auto min-w-40' : 'w-full',
          !isNextAllowed
            ? 'text-gray-500 cursor-not-allowed'
            : 'text-primary-blue cursor-pointer',
        ]"
        @click="store.saveAndContinue"
        :disabled="!isNextAllowed"
        type="button"
        v-if="store.currentStep < store.totalSteps - 1"
      >
        <span class="text-sm">{{ $t("new.continue") }}</span>
        <Icon name="tabler:arrow-narrow-right" size="24" />
      </button>
      <button
        v-else
        :disabled="store.saving"
        @click="store.createArtwork"
        type="button"
        class="flex items-center justify-center space-x-1.25"
        :class="
          store.saving
            ? 'text-gray-500 cursor-not-allowed'
            : 'text-primary-blue cursor-pointer'
        "
      >
        <span class="text-sm">{{ $t("new.save_finish") }}</span>
        <BaseLoader v-if="store.saving" size="size-4 min-w-4" />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Locale } from "vue-i18n";
import type { NewArtwork } from "~~/types/artwork";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const store = useNewArtworkStore();
const step = computed<number>(() => store.currentStep);
const artwork = computed<NewArtwork>(() => store.newArtwork);
const { locale } = useI18n();

const isNextAllowed = computed<boolean>(() => {
  const a = artwork.value;
  if (!a) return false;

  switch (step.value) {
    case 0:
      return !!a.title.hy.trim();

    case 1:
      return !!(
        a.artType &&
        a.mediums.length &&
        a.genres.length &&
        a.dimensions.height > 0 &&
        a.dimensions.width > 0
      );

    case 2:
      return !!a.thumbnail;

    case 3:
      if (!a.isForSale && !a.isForSaleDigital) return true;

      const originalValid = !a.isForSale || (a.isForSale && a.price >= 500);
      const digitalValid =
        !a.isForSaleDigital || (a.isForSaleDigital && a.priceDigital >= 500);

      return originalValid && digitalValid;
    case 4:
      const location = a.location;
      if (!location) return false;

      // const cityValue = location.city?.[locale.value as Locale];
      const countryCode = location.countryCode;
      const venueTitle = location.venue?.title;
      const venueType = location.venue?.type;

      return !!(countryCode?.trim() && venueTitle?.trim() && venueType);
    default:
      return false;
  }
});

defineProps<{ stepTitle: string }>();

const handleClose = () => {
  store.closeNewArtwork();
};
</script>
